import React, { useState, useEffect } from 'react';
import api from '../api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, isSameDay, addMonths, subMonths, isWithinInterval } from 'date-fns';
import { anniversariesOn } from '../workAnniversaries';

// Work anniversary marker: a small award ribbon.
const AnniversaryIcon = (
  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ flexShrink: 0, marginTop: '1px' }} aria-hidden="true">
    <circle cx="12" cy="8" r="6" /><path d="M15.5 13.5L17 22l-5-3-5 3 1.5-8.5" />
  </svg>
);
const anniversaryColours = { bg: 'rgba(45,106,79,0.12)', text: 'var(--success)' };

const TeamCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [loading, setLoading] = useState(true);

  const typeColours = {
    holiday: { bg: 'rgba(38,34,97,0.12)', text: 'var(--navy)', label: 'Holiday' },
    sick: { bg: 'rgba(58,54,128,0.12)', text: 'var(--navy-light)', label: 'Sick' },
    awol: { bg: 'rgba(237,28,36,0.12)', text: 'var(--danger)', label: 'AWOL' },
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
        const [cal, bdays, annivs] = await Promise.all([
          api.get(`/api/holidays/calendar?from=${from}&to=${to}`),
          api.get('/api/users/birthdays'),
          // Never let anniversaries stop the absences showing.
          api.get('/api/users/anniversaries').catch(() => ({ data: [] })),
        ]);
        setEvents(cal.data);
        setBirthdays(bdays.data);
        setAnniversaries(annivs.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentMonth]);

  const getBirthdaysForDay = (day) =>
    birthdays.filter(b => b.bmonth === day.getMonth() + 1 && b.bday === day.getDate());

  const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const monthAnniversaries = days.flatMap(day =>
    anniversariesOn(anniversaries, day).map(a => ({ ...a, date: format(day, 'EEE dd/MM') })));
  const startDow = startOfMonth(currentMonth).getDay(); // 0 = Sun
  const adjustedStart = startDow === 0 ? 6 : startDow - 1; // Mon-based

  const getEventsForDay = (day) => {
    return events.filter(e => {
      const start = new Date(e.start_date + "T00:00:00");
      const end = new Date(e.end_date + "T00:00:00");
      return isWithinInterval(day, { start, end });
    });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Team Calendar</h1>
          <p className="page-subtitle">Approved absences across the team</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setCurrentMonth(m => subMonths(m, 1))}>&#8592;</button>
          <span style={{ fontSize: '18px', fontWeight: '600', minWidth: '160px', textAlign: 'center' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button className="btn btn-outline btn-sm" onClick={() => setCurrentMonth(m => addMonths(m, 1))}>&#8594;</button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
        {Object.entries(typeColours).map(([key, val]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: val.bg, border: `1px solid ${val.text}` }} />
            <span style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{val.label}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: anniversaryColours.bg, border: `1px solid ${anniversaryColours.text}`, color: anniversaryColours.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{AnniversaryIcon}</div>
          <span style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>Work anniversary</span>
        </div>
      </div>

      <div className="card">
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid var(--border)' }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} style={{ padding: '10px', textAlign: 'center', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--grey-mid)', fontWeight: '500' }}>{d}</div>
          ))}
        </div>

        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {/* Empty cells before month start */}
            {Array.from({ length: adjustedStart }).map((_, i) => (
              <div key={`empty-${i}`} style={{ minHeight: '100px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--grey-light)' }} />
            ))}

            {days.map((day, idx) => {
              const isWeekendDay = isWeekend(day);
              const dayEvents = getEventsForDay(day);
              const dayBirthdays = getBirthdaysForDay(day);
              const dayAnniversaries = anniversariesOn(anniversaries, day);
              const isToday = isSameDay(day, new Date());

              return (
                <div key={idx} style={{
                  minHeight: '100px',
                  borderRight: '1px solid var(--border)',
                  borderBottom: '1px solid var(--border)',
                  padding: '8px',
                  background: isWeekendDay ? 'var(--grey-light)' : 'var(--white)',
                  position: 'relative'
                }}>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%',
                    background: isToday ? 'var(--navy)' : 'transparent',
                    color: isToday ? 'var(--white)' : isWeekendDay ? 'var(--grey-mid)' : 'var(--navy)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: isToday ? '600' : '400',
                    marginBottom: '4px'
                  }}>
                    {format(day, 'd')}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {dayEvents.map((e, i) => {
                      const colours = typeColours[e.absence_type] || typeColours.holiday;
                      return (
                        <div key={i} style={{
                          background: colours.bg,
                          color: colours.text,
                          borderRadius: '3px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={`${e.staff_name} - ${colours.label}`}>
                          {e.staff_name.split(' ')[0]}
                        </div>
                      );
                    })}
                    {dayBirthdays.map((b, i) => (
                      <div key={`b-${i}`} style={{
                        background: 'rgba(219,163,5,0.15)', color: '#9a6f00',
                        borderRadius: '3px', padding: '2px 6px', fontSize: '10px', fontWeight: '500',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }} title={`${b.name} — Birthday`}>
                        🎂 {b.name.split(' ')[0]}
                      </div>
                    ))}
                    {/* Name and years on two short lines, truncated like the other chips. The full
                        list under the calendar keeps the years readable on a phone. */}
                    {dayAnniversaries.map(a => (
                      <div key={`a-${a.id}`} style={{
                        background: anniversaryColours.bg, color: anniversaryColours.text,
                        borderRadius: '3px', padding: '2px 6px', fontSize: '10px', fontWeight: '500', lineHeight: 1.3, overflow: 'hidden'
                      }} title={`${a.label} (work anniversary)`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          {AnniversaryIcon}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name},</span>
                        </div>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.years} {a.years === 1 ? 'year' : 'years'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* This month's work anniversaries in full (calendar cells are too narrow on a phone). */}
      {!loading && monthAnniversaries.length > 0 && (
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header"><h3 className="card-title">Work anniversaries in {format(currentMonth, 'MMMM')}</h3></div>
          <div className="card-body" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
            {monthAnniversaries.map(a => (
              <div key={`${a.id}-${a.date}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px', color: 'var(--navy)' }}>
                <span style={{ color: anniversaryColours.text, display: 'flex' }}>{AnniversaryIcon}</span>
                <span style={{ color: 'var(--grey-mid)', minWidth: '78px' }}>{a.date}</span>
                <span>{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCalendar;
